<script lang="ts">
import { useCore } from "~/composables/useCore";
import { AccountTypeEnum } from "~~/enums/AccountType";
import { EntityTypeEnum } from "~~/enums/EntityType";
import { OpeningCompanyStepEnum } from "~~/enums/OpeningCompanyStep";
import { OpeningIndividualStepEnum } from "~~/enums/OpeningIndividualStep";

definePageMeta({
  layout: "default",
});

export default {
  data() {
    return {
      isAccountTypeSelectionVisible: true,
      isIndividualFormVisible: false,
      isCompanyFormVisible: false,
      tabStep: 0,
      accountType: undefined as AccountTypeEnum | undefined,
      countryList: [],
      preferredLanguageList: [],
      user: {},
      managerId: undefined as string | undefined,
      businessFinderId: undefined as string | undefined,
      isFullRegistration: undefined as boolean | undefined,
      helper: useHelper(),
      AccountTypeEnum: AccountTypeEnum,
    };
  },
  computed: {
    tabItems() {
      const tabs = this.getTabs();
      return this.$vuetify.display.mdAndUp
        ? tabs
        : tabs.filter((tab) => tab.value === this.tabStep);
    },
    buttonSize() {
      return this.helper.defaultButtonSize();
    },
  },
  methods: {
    getUrlParams() {
      this.managerId = this.$route.query.managerId as string;

      this.isFullRegistration =
        (this.$route.query.isFullRegistration as string | undefined) ==
          "true" ?? false;

      if (!this.helper.isValidGUID(this.managerId)) this.managerId = undefined;

      this.businessFinderId = this.$route.query.businessFinderId as
        | string
        | undefined;
      if (!this.helper.isValidGUID(this.businessFinderId))
        this.businessFinderId = undefined;
    },
    async getAllData() {
      const isLogged = await useUser().getUserIsLoggedIn();
      if (isLogged) {
        this.user = await useUser().getUserInformation();
      }
      this.countryList = await useCore().getCountryList();
      this.preferredLanguageList = await useCore().getLanguageList();
    },
    getTabs() {
      let tabs = [
        {
          id: "accountType",
          title: this.$t("open.tabs.accountType"),
          value: 0,
        },
      ];

      if (this.tabStep === 0 && !this.managerId) return [];

      if (this.tabStep === 0) return tabs;

      tabs = tabs.concat(
        this.accountType === AccountTypeEnum.Individual
          ? this.individualTabs()
          : this.companyTabs()
      );

      if (
        this.accountType === AccountTypeEnum.Individual &&
        this.tabStep === OpeningIndividualStepEnum.Confirmation
      ) {
        tabs = tabs.concat([
          {
            id: "greetings",
            title: this.$t("open.tabs.individual.confirmation"),
            value: OpeningIndividualStepEnum.Confirmation,
          },
        ]);
      }

      if (
        this.accountType === AccountTypeEnum.Company &&
        this.tabStep === OpeningCompanyStepEnum.Confirmation
      ) {
        tabs = tabs.concat([
          {
            id: "greetings",
            title: this.$t("open.tabs.company.confirmation"),
            value: OpeningCompanyStepEnum.Confirmation,
          },
        ]);
      }

      return tabs;
    },
    individualTabs() {
      return [
        {
          id: "contactInformation",
          title: this.$t("open.tabs.individual.contactInformation"),
          value: OpeningIndividualStepEnum.ContactInformation,
        },
        {
          id: "additionalInformation",
          title: this.$t("open.tabs.individual.additionalInformation"),
          value: OpeningIndividualStepEnum.AdditionalInformation,
        },
      ];
    },
    companyTabs() {
      return [
        {
          id: "companyInformation",
          title: this.$t("open.tabs.company.companyInformation"),
          value: OpeningCompanyStepEnum.CompanyInformation,
        },
        {
          id: "contactInformation",
          title: this.$t("open.tabs.company.contactInformation"),
          value: OpeningCompanyStepEnum.ContactInformation,
        },
        {
          id: "additionalInformation",
          title: this.$t("open.tabs.company.additionalInformation"),
          value: OpeningCompanyStepEnum.AdditionalInformation,
        },
      ];
    },
    validateAccountTypeAndGoToNextStep(selectedAccountType: AccountTypeEnum) {
      this.isAccountTypeSelectionVisible = false;
      this.accountType = selectedAccountType;

      if (this.accountType === AccountTypeEnum.Individual) {
        this.isIndividualFormVisible = true;
        useProspect().setProspectType(EntityTypeEnum.Individual);
      }

      if (this.accountType === AccountTypeEnum.Company) {
        this.isCompanyFormVisible = true;
        useProspect().setProspectType(EntityTypeEnum.Corporate);
      }

      this.navigateToTab(1);
    },
    navigateToTab(tabNumber: number) {
      if (tabNumber === 0) {
        this.isIndividualFormVisible = false;
        this.isCompanyFormVisible = false;
        this.isAccountTypeSelectionVisible = true;
        this.accountType = undefined;
      }

      this.tabStep = tabNumber;
    },
  },
  async created() {
    this.getUrlParams();
    await this.getAllData();
  },
};
</script>

<template>
  <div class="flex-fill">
    <!-- Tabs header -->
    <v-tabs
      class="mb-3"
      color="#E05C91"
      align-tabs="center"
      v-model="tabStep"
      :items="tabItems"
    >
      <v-tab
        :ripple="false"
        @click.native.prevent.stop.capture
        v-for="item in tabItems"
        class="open-tab"
        :value="item.value"
      >
        <div>{{ item.title }}</div>
      </v-tab>
    </v-tabs>

    <v-card
      v-if="tabStep == 0 && !managerId && false"
      width="100%"
      rounded
      elevation="6"
      class="pa-4 mx-auto"
    >
      <v-card-title>
        <h5>{{ $t("open.invitationOnly") }}</h5>
      </v-card-title>
      <v-card-text>
        <div
          class="invitation-message"
          v-html="$t('open.invitationOnlyMessage')"
        ></div>
      </v-card-text>
    </v-card>

    <template v-else>
      <!-- Choosing Individual or Company account -->
      <v-card
        v-if="isAccountTypeSelectionVisible"
        width="100%"
        rounded
        elevation="6"
        class="pa-4 mx-auto"
      >
        <v-card-title>
          <h5>{{ $t("open.accountType.title") }}</h5>
          <h6 class="text-wrap">{{ $t("open.accountType.subtitle") }}</h6>
        </v-card-title>
        <v-row class="card-center">
          <v-col>
            <v-card
              class="account-type-card"
              v-bind:class="{
                'account-type-card-selected':
                  accountType === AccountTypeEnum.Individual,
              }"
              @click="
                validateAccountTypeAndGoToNextStep(AccountTypeEnum.Individual)
              "
            >
              <v-row>
                <v-col cols="12" md="2">
                  <v-icon class="cim-large-icon">mdi-account-outline</v-icon>
                </v-col>
                <v-col cols="12" md="10">
                  <h3>{{ $t("open.accountType.optionIndividual") }}</h3>
                  <span
                    v-html="$t('open.accountType.optionIndividualInformation')"
                  ></span>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col>
            <v-card
              class="account-type-card account-type-card-company"
              v-bind:class="{
                'account-type-card-selected':
                  accountType === AccountTypeEnum.Company,
              }"
              @click="
                validateAccountTypeAndGoToNextStep(AccountTypeEnum.Company)
              "
            >
              <v-row>
                <v-col cols="12" md="2">
                  <v-icon class="cim-large-icon">mdi-briefcase-outline</v-icon>
                </v-col>
                <v-col cols="12" md="10">
                  <h3>{{ $t("open.accountType.optionCorporate") }}</h3>
                  <span
                    v-html="$t('open.accountType.optionCorporateInformation')"
                  ></span>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="text-center">
            <span v-html="$t('open.accountType.warningPotential')"></span>
          </v-col>
        </v-row>
      </v-card>
    </template>

    <!-- Individual form -->
    <OpenIndividualForm
      v-if="isIndividualFormVisible"
      :country-list="countryList"
      :prefered-language-list="preferredLanguageList"
      :user-email="user?.emailAddress"
      :managerId="managerId"
      :is-full-registration="isFullRegistration"
      :businessFinderId="businessFinderId"
      @changeTab="navigateToTab($event)"
    >
    </OpenIndividualForm>

    <!-- Company form -->
    <OpenCompanyForm
      v-if="isCompanyFormVisible"
      :country-list="countryList"
      :prefered-language-list="preferredLanguageList"
      :user-email="user?.emailAddress"
      :managerId="managerId"
      :is-full-registration="isFullRegistration"
      :businessFinderId="businessFinderId"
      @changeTab="navigateToTab($event)"
    >
    </OpenCompanyForm>
  </div>
</template>

<style scoped lang="scss">
.v-card {
  background: rgba(255, 255, 255, 0.7);
}

.v-card-title {
  text-align: center;

  & > h5 {
    color: #E05C91;
    padding-bottom: 15px;
    white-space: normal;
    line-height: 24px;
  }
}

.v-card-actions {
  float: right;
}

.cim-large-icon {
  font-size: 50px;
}

.open-tab {
  cursor: default;

  &:hover > {
    :deep(.v-btn__overlay) {
      opacity: 0;
    }
  }

  @media screen and (min-width: 960px) and (max-width: 1280px) {
    font-size: 12px;
    padding: 0 10px;
  }
}

.account-type-card {
  background-color: #f9ebf4;
  border: 2px solid #f9ebf4;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  height: 100%;

  &:hover,
  &-selected {
    border-color: #E05C91;
  }

  &-company {
    background-color: #ebf3fc;
    border-color: #ebf3fc;

    &:hover {
      border-color: #8ab8e5;
    }

    &.account-type-card-selected {
      border-color: #8ab8e5;
    }
  }
}

$card-background-color: #fafbfc;
.company-account-type-card {
  border: solid 2px $card-background-color;
  background-color: $card-background-color;
  min-height: 150px;

  &.account-type-card-selected {
    border-color: #8ab8e5;
  }
}

.company-account-type-card:hover,
.company-account-type-card-selected {
  border-color: #E05C91;
  cursor: pointer;
}

.invitation-message {
  line-height: 1.5em;
}
</style>
